import React, { useEffect, useState } from 'react';

import Link from 'components/shared/Link';
import Button from 'components/shared/Button';
import Modal from 'components/shared/Modal';

import Spinner from 'components/shared/Spinner';

import wallPreviewModule from 'domains/wall_previews';
import rooms from 'domains/wall_preview_rooms';

import { useAppDispatch, useAppSelector } from 'utils/hooks';

type ShareWallPreviewModalProps = {
  onClose: () => void;
};

function ShareWallPreviewModal(props: ShareWallPreviewModalProps) {
  const { onClose } = props;

  const dispatch = useAppDispatch();

  const whichWallPreview = useAppSelector(rooms.selectors.whichWallPreview);

  const latestPreview = useAppSelector(wallPreviewModule.selectors.latest);
  const previewStatus = useAppSelector(wallPreviewModule.selectors.status);

  const [previewName, setPreviewName] = useState('');
  const [sharePreviewLink, setSharePreviewLink] = useState('');
  const [isGeneratingPreviewLink, setIsGeneratingPreviewLink] = useState(false);
  const [sharePreviewError, setSharePreviewError] = useState(false);

  if (!whichWallPreview) {
    return null;
  }

  const savePreview = () => {
    dispatch(wallPreviewModule.actions.saveWallPreview.pending(previewName));
    setIsGeneratingPreviewLink(true);
  };

  // Set values when we have successful preview generated
  useEffect(() => {
    if (isGeneratingPreviewLink && latestPreview && latestPreview.url) {
      setSharePreviewLink(latestPreview.url);
      setPreviewName('');
      setIsGeneratingPreviewLink(false);
    }
  }, [latestPreview, isGeneratingPreviewLink]);

  // Handle an error while generating preview
  useEffect(() => {
    if (isGeneratingPreviewLink && previewStatus === 'error') {
      setSharePreviewLink('');
      setPreviewName('');
      setSharePreviewError(true);
      setIsGeneratingPreviewLink(false);
    }
  }, [previewStatus, isGeneratingPreviewLink]);

  const disableButtons = isGeneratingPreviewLink || !!sharePreviewLink;

  const primaryFooterGroup = (
    <React.Fragment>
      <Button type="confirm" onClick={() => savePreview()} disabled={disableButtons}>
        Save
      </Button>
      <Button type="cancel" onClick={onClose} disabled={disableButtons}>
        Cancel
      </Button>
    </React.Fragment>
  );

  return (
    <Modal
      title="Share Preview"
      labelledBy="generate-share-preview-modal"
      modalClass={['share-preview-modal']}
      lastFocusedElementId="wall-preview-menu-trigger"
      onClose={onClose}
      primaryFooterGroup={primaryFooterGroup}
    >
      <React.Fragment>
        {!sharePreviewLink && !isGeneratingPreviewLink && !sharePreviewError && (
          <div className="share-preview-info">
            <h2>Share an image of this wall preview with others</h2>
            <form onSubmit={() => savePreview()}>
              <label htmlFor="share-preview-name">Set a name for this preview</label>
              <input
                type="text"
                name="share-preview-name"
                id="share-preview-name"
                value={previewName}
                onChange={(e) => setPreviewName(e.target.value)}
              />
            </form>
          </div>
        )}
      </React.Fragment>

      <React.Fragment>
        {!sharePreviewLink && isGeneratingPreviewLink && (
          <div className="share-preview-info">
            <h2>Generating preview...</h2>
            <Spinner color="#252525" size="40" />
          </div>
        )}
      </React.Fragment>

      <React.Fragment>
        {sharePreviewError && (
          <div className="share-preview-info">
            <h2>There was a problem generating your preview</h2>
            <p>
              <Link href="/wall_previews" target="_blank" rel="noopener noreferrer">
                View a list of all your previews (opens in new tab)
              </Link>
              &nbsp;or try saving it again.
            </p>
          </div>
        )}
      </React.Fragment>

      <React.Fragment>
        {sharePreviewLink && (
          <div className="share-preview-info">
            <h2>Your preview is ready!</h2>
            <img src={sharePreviewLink} alt="example of your wall preview" />
            <p>
              Public link to your wall preview:&nbsp;
              <Link
                href={sharePreviewLink}
                id="share-preview-link"
                target="_blank"
                rel="noopener noreferrer"
              >
                {sharePreviewLink}
              </Link>
            </p>
            <p>
              <Link href="/wall_previews" target="_blank" rel="noopener noreferrer">
                View a list of all your previews (opens in new tab)
              </Link>
            </p>
          </div>
        )}
      </React.Fragment>
    </Modal>
  );
}

export default ShareWallPreviewModal;
